import { Layer, useMap } from "@vis.gl/react-maplibre";
import { FC, useEffect, useId } from "react";

import { getAliveMap, useMapLibreZoom } from "@ryde/controls";

import {
  ROUTE_ARROW_ICON_ROTATE_DEGREES,
  ROUTE_ARROW_ICON_SIZE,
  ROUTE_ARROW_SYMBOL_SPACING_PX,
} from "../route-block-maplibre.constants";
import {
  ensureRouteArrowImage,
  routeArrowImageId,
} from "./maplibre-route-arrow-image.utils";

interface IMapLibreRouteArrowsProps {
  /**
   * Id of the line's `<Source>`, injected by `@vis.gl/react-maplibre` into each child that source renders. Mount this
   * among those children and never on its own: the arrows then read the geometry the line already put on the
   * map, instead of a second copy of it in a source of their own. That copy was the real per-route price of
   * arrows — its own worker-side tile index, re-built whenever the data changed — and paying it once per
   * arrowed line is what lets a crowded map keep its arrows.
   */
  source?: string;
  color: string;
  /**
   * Perpendicular shift (screen px) the arrows must share with their line, so they ride the drawn line
   * rather than the source geometry. `line-offset` moves a line layer but never its symbols.
   */
  offsetPx?: number;
  /**
   * Gap (px) between arrows along the line. Lines drawn beside each other must not share it — see
   * `TRAIL_ARROW_SYMBOL_SPACING_PX`.
   */
  spacingPx?: number;
  /**
   * Below this zoom the arrows are skipped entirely. Unset = always drawn.
   *
   * Gated twice, on purpose. Unmounting removes the symbol layer outright, so MapLibre stops laying out and
   * re-placing its quads; `minzoom` covers the gap the unmount cannot, hiding the arrows mid-gesture, since
   * the shared zoom only lands on `zoomend`.
   */
  minZoom?: number;
}

/**
 * Direction arrows along a route line — one native `symbol` layer (`symbol-placement: line`) for every line
 * on the map, planned route and GPS trail alike, so both read identically and both cost near-zero per frame.
 * MapLibre repeats the baked "►" icon at a fixed screen-px spacing and rotates it to the line's travel
 * direction automatically (no manual bearing); `icon-allow-overlap`/`icon-ignore-placement` stop
 * closely-spaced arrows from being culled — and, incidentally, skip collision detection, which is the part of
 * symbol placement that would otherwise scale badly with the number of routes. The icon is registered lazily
 * via `setMissingStyleImageResolver` (and eagerly once the style is up).
 *
 * Arrows are deliberately NOT held clear of station pins. The renderer that did that placed one HTML marker
 * per arrow and dropped any landing within a pin's radius — on a real ride (~48 stops) the exclusion zones
 * covered the whole line, leaving arrows only on the long station-free legs at the start and end. Leaflet's
 * textpath never avoided pins either, and does not need to: pins are DOM markers above the canvas and
 * station dots are a later layer, so both simply cover the arrow beneath them.
 */
export const MapLibreRouteArrows: FC<IMapLibreRouteArrowsProps> = ({
  source,
  color,
  offsetPx,
  spacingPx = ROUTE_ARROW_SYMBOL_SPACING_PX,
  minZoom,
}) => {
  const { current: mapRef } = useMap();
  const zoom = useMapLibreZoom();
  const rawId = useId();
  const layerId = `route-arrows-${rawId.replace(/:/g, "")}`;
  const imageId = routeArrowImageId(color);

  // Registered even while the arrows are gated away: the image is shared per color and cached, so paying for
  // it early means crossing the zoom threshold draws arrows on the next frame instead of waiting for the
  // v6 missing-image resolver round-trip. `style.load` covers the first paint (isStyleLoaded is still false
  // on mount) and a later tiles-service `setStyle`, which drops runtime images.
  useEffect(() => {
    const map = getAliveMap(mapRef);
    if (!map) {
      return;
    }

    const registerImage = (): void => {
      if (!map.isStyleLoaded()) {
        return;
      }
      ensureRouteArrowImage(map, { color });
    };

    registerImage();
    map.on("style.load", registerImage);
    return () => {
      getAliveMap(mapRef)?.off("style.load", registerImage);
    };
  }, [mapRef, color]);

  if (minZoom !== undefined && zoom < minZoom) {
    return null;
  }

  return (
    <Layer
      id={layerId}
      source={source}
      type="symbol"
      {...(minZoom === undefined ? {} : { minzoom: minZoom })}
      layout={{
        ["symbol-placement"]: "line",
        ["symbol-spacing"]: spacingPx,
        ["icon-image"]: imageId,
        ["icon-size"]: ROUTE_ARROW_ICON_SIZE,
        ["icon-rotate"]: ROUTE_ARROW_ICON_ROTATE_DEGREES,
        ["icon-rotation-alignment"]: "map",
        ["icon-pitch-alignment"]: "map",
        ["icon-allow-overlap"]: true,
        ["icon-ignore-placement"]: true,
        // The icon quad is shifted before it is rotated to the line bearing, so the y component lands
        // perpendicular to travel — the same direction and sign as the line layer's `line-offset`.
        // `icon-offset` is multiplied by `icon-size`, hence the division.
        ...(offsetPx
          ? {
              ["icon-offset"]: [0, offsetPx / ROUTE_ARROW_ICON_SIZE] as [
                number,
                number,
              ],
            }
          : {}),
      }}
    />
  );
};
